import { schemeCodeOf } from '../../../core/utils/scheme-code';
/**
 * The Connect page's figures and labels, ported from MedicAI Connect's own dashboard so both read the same.
 */

export interface ConnectPatient {
  id: string;
  name: string;
  surname: string;
  patientNumber: string | null;
  accountNumber: string | null;
  idNumber: string | null;
  passportNumber: string | null;
  dob: string | null;
  medicalAidNumber: string | null;
  medicalAid: string | null;
  medicalAidPlan: string | null;
  medicalSchemeCode: unknown;
  medicalPlanCode: unknown;
  medicalPlanOptionCode: unknown;
  /** The patient here in clinical, when they belong to this practice. */
  clinicalPatientId: string | null;
}

/** A draft claim waiting to go to the scheme. */
export interface ConnectClaimItem {
  batchId: string;
  patientId: string;
  patient: ConnectPatient;
  batch: { startDate: string; endDate: string };
  procedureCodeCount: number;
  icdCodeCount: number;
  maxProcedureDate: string | null;
  daysSinceService: number;
  submissionStatus: string | null;
  trackingId: string | null;
  hnetNumber: string | null;
}

/**
 * One open claim or invoice on the board. Connect builds before the per-document board sent one item per patient,
 * without the document fields, so those are optional here.
 */
export interface ConnectAgingItem {
  documentId?: string;
  documentType?: 'claim' | 'invoice';
  documentNumber?: string | null;
  trackingId?: string | null;
  totalCents?: number | null;
  schemePaidCents?: number;
  patientPaidCents?: number;
  writtenOffCents?: number;
  patientId: string;
  patient: ConnectPatient;
  openClaimCount: number;
  outstandingCents: number;
  schemeOutstandingCents: number;
  patientOutstandingCents: number;
  oldestServiceDate: string | null;
  daysSinceOldestService: number;
  oldestSubmittedDate: string | null;
  oldestSchemeDeadlineServiceDate: string | null;
  worstStatus: string | null;
  unsentPaperClaimCount: number;
}

export interface ConnectBuckets {
  incoming: ConnectClaimItem[];
  current: ConnectAgingItem[];
  '30': ConnectAgingItem[];
  '60': ConnectAgingItem[];
  '90': ConnectAgingItem[];
  '120_plus': ConnectAgingItem[];
}

/** The money picture Connect sends beside the board: this month's flows and what is still owed. */
export interface ConnectSummary {
  periodFrom: string;
  periodTo: string;
  billedCents: number;
  schemeReceivedCents: number;
  patientReceivedCents: number;
  schemeDueCents: number;
  patientDueCents: number;
  schemeDocumentCount: number;
  patientDocumentCount: number;
  openDocumentCount: number;
  patientCount: number;
}

/** Something the practice's billing office on Connect asked this practice to do for a patient. */
export interface ConnectRequest {
  id: string;
  kind: 'letter_of_motivation';
  /** The patient on Connect. */
  patientId: string;
  /** The patient here (clinicalPatientId) when they belong to this practice. */
  patient: { id: string; clinicalPatientId: string | null; name: string };
  details: { reason?: string; claimNumber?: string };
  createdAt: string;
}

export interface ConnectDashboard {
  tenantName: string;
  buckets: ConnectBuckets;
  /** Absent from Connect builds before the money summary. */
  summary?: ConnectSummary | null;
  /** Absent from Connect builds before requests. */
  requests?: ConnectRequest[];
}

/** The open request about a Connect patient, if there is one. */
export function requestFor(requests: ConnectRequest[] | undefined, connectPatientId: string): ConnectRequest | null {
  return (requests ?? []).find((r) => r.patientId === connectPatientId) ?? null;
}

export function requestTitle(request: ConnectRequest): string {
  return request.kind === 'letter_of_motivation' ? 'Letter of motivation requested' : 'Request from billing';
}

/** An amount in cents as `R38,129.86`, `−R540.20`. */
export function formatCents(cents: number | string | null | undefined): string {
  const n = cents == null || cents === '' ? 0 : Number(cents);
  const value = Number.isFinite(n) ? n / 100 : 0;
  const grouped = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${value < 0 && grouped !== '0.00' ? '−' : ''}R${grouped}`;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A YYYY-MM-DD date as "21 Sep", read from the string so no time zone shifts the day. */
export function formatDayMonth(iso: string | null | undefined): string {
  if (!iso) return '-';
  const [, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d} ${SHORT_MONTHS[m - 1]}`;
}

/** A date of birth as DD/MM/YYYY, stored either that way or as an ISO date; null when it cannot be read. */
export function formatDob(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : null;
}

const DAY_MS = 86_400_000;
const SAST_OFFSET_MS = 2 * 60 * 60 * 1000;

function toUtc(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** The same day `months` later, clamped to the end of a shorter month. */
function addCalendarMonths(iso: string, months: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, Math.min(d, lastDay))).toISOString().slice(0, 10);
}

/** Today's calendar date in SAST. */
export function sastTodayIso(now: Date = new Date()): string {
  return new Date(now.getTime() + SAST_OFFSET_MS).toISOString().slice(0, 10);
}

export interface StalenessChip {
  tone: 'warning' | 'danger';
  label: string;
}

/** Schemes accept a claim for 4 months: amber from 3 months, red once that has passed. */
export function claimStaleness(serviceDate: string | null | undefined, today: string): StalenessChip | null {
  if (!serviceDate) return null;
  const stale = addCalendarMonths(serviceDate, 4);
  if (today > stale) return { tone: 'danger', label: 'Past 4 months' };
  if (today < addCalendarMonths(serviceDate, 3)) return null;
  const days = Math.round((toUtc(stale) - toUtc(today)) / DAY_MS);
  if (days === 0) return { tone: 'warning', label: 'Stale today' };
  return { tone: 'warning', label: `Stale in ${days} ${days === 1 ? 'day' : 'days'}` };
}

/** The card's flag for paper claims not yet sent to the scheme, or null. */
export function unsentPaperClaimsText(count: number | null | undefined): string | null {
  if (!count || count <= 0) return null;
  return count === 1 ? 'Paper claim not yet sent' : `${count} paper claims not yet sent`;
}

/** Rejected, short-paid, or never sent to MediKredit. */
const ATTENTION_STATUSES = new Set(['rejected', 'partial', 'failed']);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * The tiles at the top of the page: money from Connect's summary, counts from the board. Without a summary (an older
 * Connect) what is owed is added up from the board, and billed and received stay blank.
 */
export function dashboardStats(b: ConnectBuckets, summary?: ConnectSummary | null) {
  const open = [...b.current, ...b['30'], ...b['60'], ...b['90'], ...b['120_plus']];
  const schemeDueCents = summary ? summary.schemeDueCents : open.reduce((s, d) => s + (d.schemeOutstandingCents ?? 0), 0);
  const patientDueCents = summary ? summary.patientDueCents : open.reduce((s, d) => s + (d.patientOutstandingCents ?? 0), 0);
  return {
    incoming: b.incoming.length,
    billedCents: summary ? summary.billedCents : null,
    receivedCents: summary ? summary.schemeReceivedCents + summary.patientReceivedCents : null,
    schemeReceivedCents: summary ? summary.schemeReceivedCents : null,
    patientReceivedCents: summary ? summary.patientReceivedCents : null,
    schemeDueCents,
    schemeDocuments: summary ? summary.schemeDocumentCount : open.filter((d) => (d.schemeOutstandingCents ?? 0) > 0).length,
    patientDueCents,
    patientDocuments: summary ? summary.patientDocumentCount : open.filter((d) => (d.patientOutstandingCents ?? 0) > 0).length,
    outstandingCents: schemeDueCents + patientDueCents,
    openDocuments: summary ? summary.openDocumentCount : open.reduce((s, d) => s + (d.openClaimCount ?? 1), 0),
    patients: summary ? summary.patientCount : new Set(open.map((d) => d.patientId)).size,
    attention: open.filter((d) => ATTENTION_STATUSES.has(d.worstStatus ?? '')).length,
    overdue: b['90'].length + b['120_plus'].length,
    month: summary ? MONTHS[Number(summary.periodFrom.slice(5, 7)) - 1] ?? null : null,
  };
}

/** "Claim · tracking number" or "Invoice INV-…"; null for an item from an older Connect that names no document. */
export function documentTitle(item: ConnectAgingItem): string | null {
  if (!item.documentType) return null;
  if (item.documentType === 'invoice') return item.documentNumber ? `Invoice ${item.documentNumber}` : 'Invoice';
  const ref = item.trackingId ?? item.documentNumber;
  return ref ? `Claim · ${ref}` : 'Claim';
}

export type Tone = 'danger' | 'warning' | 'info' | 'success' | 'neutral';

/** A patient's pill: their worst claim, and "Part approved" when the patient still owes. */
export function agingStatus(worstStatus: string | null, patientOwes: boolean): { label: string; variant: Tone } {
  switch (worstStatus) {
    case 'failed': return { label: 'Not sent', variant: 'danger' };
    case 'rejected': return { label: 'Rejected', variant: 'danger' };
    case 'partial': return { label: 'Partial', variant: 'warning' };
    case 'reversed': return { label: 'Reversed', variant: 'neutral' };
    case 'processing': return { label: 'Processing', variant: 'info' };
    case 'submitted': return { label: 'Submitted', variant: 'info' };
    case 'paper_claim': return { label: 'Paper claim', variant: 'neutral' };
    case 'issued': return { label: 'Issued', variant: 'info' };
    case 'approved':
      return patientOwes ? { label: 'Part approved', variant: 'warning' } : { label: 'Approved', variant: 'success' };
    default: return { label: 'Open', variant: 'neutral' };
  }
}

export type ClaimCardStatus = 'approved' | 'rejected' | 'claim-partial' | 'submitted' | 'paper-claim' | 'complete' | 'partial' | 'missing';

/** An incoming claim's state: the scheme's answer once it has one, otherwise what it still needs. */
export function claimCardStatus(claim: ConnectClaimItem): ClaimCardStatus {
  switch (claim.submissionStatus) {
    case 'approved': return 'approved';
    case 'rejected': return 'rejected';
    case 'partial': return 'claim-partial';
    case 'paper_claim': return 'paper-claim';
    case 'submitted': return 'submitted';
  }
  const p = claim.patient;
  const linked = !!schemeCodeOf(p.medicalSchemeCode) && !!schemeCodeOf(p.medicalPlanCode) && !!schemeCodeOf(p.medicalPlanOptionCode);
  if (!linked) return 'missing';
  return (claim.icdCodeCount ?? 0) > 0 ? 'complete' : 'partial';
}

/** An unlinked claim whose patient has a file in this practice, where the medical details can be linked. */
export function canLinkDetails(claim: ConnectClaimItem): boolean {
  return claimCardStatus(claim) === 'missing' && !!claim.patient.clinicalPatientId;
}
